import { useEffect, useState } from "react";
import RichText from "@/components/RichText";
import axios from "axios";
import { Loader2, ScrollText } from "lucide-react";
import { usePublicBranding, useBrandTheme } from "@/components/Brand";
import { LegalLinks } from "@/pages/Legal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const wrap = "[overflow-wrap:anywhere]";

/** /policies/<programme>: the programme's current terms & policies, readable by anyone -- the page the "terms & policies" link at the foot of its emails opens. */
export default function PoliciesPage() {
  const slug = (window.location.pathname.split("/").filter(Boolean)[1] || "").toLowerCase();
  const branding = usePublicBranding(slug);
  useBrandTheme(branding);
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!slug) { setItems([]); return; }
    axios.get(`${BACKEND}/api/public/policies`, { params: { slug } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [slug]);
  useEffect(() => { document.title = `${branding?.name ? `${branding.name} — ` : ""}Terms & policies`; }, [branding?.name]);
  const logo = branding?.logoUrl ? `${BACKEND}${branding.logoUrl}` : "";
  return (
    <div className="min-h-screen bg-[var(--hp-bg)] text-[var(--hp-text)]" data-testid="policies-page">
      <header className="border-b border-[var(--hp-border)] bg-[var(--hp-card)]">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4">
          {logo && <img src={logo} alt="" className="h-10 w-auto max-w-[8rem] rounded-md bg-white object-contain p-0.5" />}
          <p className={`font-head text-lg font-bold text-[var(--hp-heading)] ${wrap}`} data-testid="policies-programme">{branding?.name || ""}</p>
        </div>
      </header>
      <main className="mx-auto max-w-3xl space-y-6 px-4 py-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--hp-profit)]">Our programme</p>
          <h1 className="mt-1 flex items-center gap-2 font-head text-3xl font-extrabold tracking-tight text-[var(--hp-heading)]"><ScrollText className="h-7 w-7 text-[var(--hp-accent)]" /> Terms &amp; policies</h1>
        </div>
        {items === null ? <p className="inline-flex items-center gap-2 text-sm text-[var(--hp-muted)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
          : items.length === 0 ? <p className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 text-sm text-[var(--hp-muted)]" data-testid="policies-empty">There are no published terms or policies for this programme yet.</p>
          : items.map((p) => (
            <article key={p.id} className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5" data-testid="policies-item">
              <h2 className={`font-head text-xl font-bold text-[var(--hp-heading)] ${wrap}`}>{p.title}</h2>
              <RichText html={p.bodyHtml} text={p.body} className={`mt-2 text-sm leading-relaxed ${wrap}`} />
            </article>
          ))}
        <p className="text-xs text-[var(--hp-muted)]"><LegalLinks /></p>
      </main>
    </div>
  );
}
